import { useState } from 'react'
import isEqual from 'react-fast-compare'

import useDeepEffect from '@hooks/useDeepEffect'

type FiltersWithOffset = {
  offset?: number,
}

export type UseLoadMoreOptions = {
  filters?: object,
  limit?: number,
  loadedCount: number,
  performHttpRequests?: boolean,
}

export type UseLoadMorePayload = {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filtersWithOffset: FiltersWithOffset,
  limit: number,
  offset: number,
  performHttpRequests: boolean,
}

const checkPerformHttpRequests = (options: UseLoadMoreOptions, filtersWithOffset: FiltersWithOffset): boolean => {
  const performHttpRequests = options?.performHttpRequests ?? false

  const filters = options?.filters ?? {}

  const { offset, ...filtersWithoutOffset } = filtersWithOffset

  return performHttpRequests && offset !== undefined && isEqual(filters, filtersWithoutOffset)
}

function useLoadMore(options: UseLoadMoreOptions): UseLoadMorePayload {
  const {
    filters = {},
    loadedCount,
    limit = 100,
  } = options || {}

  const [filtersWithOffset, setFiltersWithOffset] = useState<FiltersWithOffset>(filters)
  const { offset = 0 } = filtersWithOffset || {}

  // Setup or Reset Filters
  useDeepEffect(() => {
    setFiltersWithOffset({
      ...filters,
      offset: 0,
    })
  }, [filters])

  return {
    callbacks: {
      loadMore: () => setFiltersWithOffset(prevFilters => ({
        ...prevFilters,
        offset: (prevFilters.offset || 0) + limit,
      })),
    },
    canLoadMore: loadedCount >= (limit + offset),
    filtersWithOffset,
    limit,
    offset,
    performHttpRequests: checkPerformHttpRequests(options, filtersWithOffset),
  }
}

export default useLoadMore
